import React, { useMemo, useEffect, useState } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MultiFormSubmission from '../MultiFormSubmission/MultiFormSubmission';
import Grid from 'apollo-react/components/Grid';
import CircularProgress from 'apollo-react/components/CircularProgress';
import RequestAuditTrails from '../../../common/RequestAuditTrails';
import AdditionalDocuments from '../../../common/AdditionalDocuments';
import RequestClarificationWaves from '../../../common/RequestClarificationWaves';
import LinkedStudiesView from '../../../common/LinkedStudies/LinkedStudiesView';
import RequestBadges from '../../../common/RequestBadges/RequestBadges';
import useMyRequestDetails from '../../../../hooks/features/customer/useMyRequestDetails';
import useLinkedStudies from '../../../../hooks/features/customer/useLinkedStudies';
import useSubformCommentStatus from '../../../../hooks/features/comments/useSubformCommentStatus';
import { usePageTitle } from '../../../../contexts/PageTitleContext';
import { getCurrentUser } from '../../../../services/authService';
import {
  STAGE_TYPES,
  APPROVAL_TYPE_CODES,
} from '../../../../constants/workflow.constant';

/**
 * MyRequestDetails - Customer's Request Details Page
 *
 * Shows request details and handles send-back editing with multi-form support.
 * Uses MultiFormSubmission component for consistent structure (DRY):
 * - Waiting for review: read-only view (disabled=true)
 * - Send-back: editable submission (disabled=false)
 * - Submission stage: read-only view (disabled=true)
 * - Uses isRequestActive from location state to determine if the request is assigned/active
 */
const MyRequestDetails = () => {
  const { id } = useParams();
  const { t } = useTranslation();

  const location = useLocation();

  const { request, requestProgress, loading, error } = useMyRequestDetails(id);
  // isRequestActive: true = from Assigned tab, false = from In Progress/Approved/Rejected
  // If no state (direct navigation), fallback to requestProgress.active
  const explicitState = location.state?.isRequestActive;
  const isRequestActive =
    typeof explicitState === 'boolean'
      ? explicitState
      : (requestProgress?.active ?? false);
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get('draft_id');
  const draftId = draftIdParam ? parseInt(draftIdParam, 10) : null;
  const [currentUserEmail, setCurrentUserEmail] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      const data = await getCurrentUser();
      setCurrentUserEmail(data?.user?.email || null);
    };
    fetchUser();
  }, []);

  // Fetch linked studies for this request
  const {
    linkedStudies,
    linkedStudiesEnabled,
    loading: linkedStudiesLoading,
    error: linkedStudiesError,
  } = useLinkedStudies(id);

  // Set dynamic page title based on request name
  const { setPageTitle, clearPageTitle } = usePageTitle();

  useEffect(() => {
    if (request?.study_name) {
      setPageTitle(request.study_name);
    }
    return () => clearPageTitle();
  }, [request?.study_name, setPageTitle, clearPageTitle]);

  const isSubmissionStage = useMemo(
    () =>
      requestProgress?.StageRelation?.Child?.StageType?.type ===
      STAGE_TYPES.SUBMISSION,
    [requestProgress]
  );

  // Detect if we arrived at this stage via send-back action
  const isSendBack = useMemo(
    () =>
      requestProgress?.StageRelation?.Stage?.ApprovalType?.code ===
      APPROVAL_TYPE_CODES.SEND_BACK,
    [requestProgress]
  );

  // Pre-fill forms with any previously saved filled forms (e.g., send-back/resume)
  const existingFilledForms = useMemo(() => {
    if (!request?.requests_progresses) return {};

    const filledForms = {};
    request.requests_progresses.forEach((rp) => {
      if (rp.end_user_filled_form) {
        const formId = rp.end_user_filled_form.form_id;
        if (formId) {
          filledForms[formId] = rp.end_user_filled_form;
        }
      }
    });
    return filledForms;
  }, [request]);

  // Extract all unique comments from all progress records
  // Used to display actual comment content in FormCrafterListView
  const allComments = useMemo(() => {
    if (!request?.requests_progresses) return [];

    const commentsMap = new Map(); // Use Map to dedupe by comment id
    request.requests_progresses.forEach((rp) => {
      const filledForm = rp.end_user_filled_form;
      const comments = filledForm?.comments || [];
      comments.forEach((comment) => {
        if (!commentsMap.has(comment.id)) {
          commentsMap.set(comment.id, comment);
        }
      });
    });
    return Array.from(commentsMap.values());
  }, [request]);

  // Extract field-level comment status (for icons) - flattened by subform_id
  const fieldCommentStatus = useMemo(() => {
    const status = {};
    allComments.forEach((comment) => {
      if (comment.subForm_id) {
        status[comment.subForm_id] = true;
      }
    });
    return status;
  }, [allComments]);

  // Fetch subform-level comment status for navigation indicators
  const { subformCommentStatus, refreshSubformCommentStatus } =
    useSubformCommentStatus(id);

  // Merge field-level and subform-level comment statuses
  const commentsStatus = useMemo(
    () => ({ ...fieldCommentStatus, ...subformCommentStatus }),
    [fieldCommentStatus, subformCommentStatus]
  );

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
        {t('Error loading request')}: {error}
      </div>
    );
  }

  // Get the current stage ID that user needs to interact with
  const currentStageId = requestProgress?.StageRelation?.Child?.id;

  const workflowVersionId =
    request?.workflow_version_id ||
    request?.WorkflowVersion?.id ||
    requestProgress?.StageRelation?.Child?.wf_version_id ||
    requestProgress?.StageRelation?.Child?.WorkflowVersion?.id ||
    null;

  const renderFormContent = () => {
    // Editable logic:
    // - Always editable if it's a send-back (for resubmission)
    // - Editable if from Assigned tab (isRequestActive=true) OR has draft_id (failed payment redirect)
    // - Read-only if explicitly from In Progress/Approved/Rejected tabs (isRequestActive=false)
    // - Otherwise, rely on requestProgress?.active
    const isViewOnlyTab = location.state?.isRequestActive === false;
    const isEditableContext =
      isRequestActive || draftId || isSendBack || requestProgress?.active;
    const isEditable = isSubmissionStage && !isViewOnlyTab && isEditableContext;
    // Send-back: list view with per-field editing
    // Regular submission: full form renderer
    // View-only: list view
    const useListView = !isEditable || isSendBack;
    // Comments only enabled during send-back (not regular submission stages)
    const enableAddComment = isSendBack && isEditable;
    return (
      <MultiFormSubmission
        workflowVersionId={workflowVersionId}
        requestId={id}
        requestProgressId={requestProgress?.id}
        stageId={currentStageId}
        existingFilledForms={existingFilledForms}
        draftId={draftId}
        disabled={!isEditable}
        useListView={useListView}
        isEditable={isEditable}
        showValidationIndicators={isEditable}
        commentsStatus={commentsStatus}
        allComments={allComments}
        enableAddComment={enableAddComment}
        topSlot={<RequestBadges requestProgressId={requestProgress?.id} />}
        requestAuditTrailsSlot={<RequestAuditTrails requestId={id} />}
        requestProgress={requestProgress}
        requestDateEnded={request?.date_ended}
        currentUserEmail={currentUserEmail}
        isRequestActive={isRequestActive}
        onSubformCommentAdded={refreshSubformCommentStatus}
        additionalDocumentsSlot={
          <AdditionalDocuments
            requestId={id}
            requestDateEnded={request?.date_ended}
            requestProgress={requestProgress}
            currentUserEmail={currentUserEmail}
          />
        }
        linkedStudiesViewSlot={
          linkedStudiesEnabled && (
            <LinkedStudiesView
              linkedStudies={linkedStudies}
              loading={linkedStudiesLoading}
              error={linkedStudiesError}
            />
          )
        }
        requestClarificationWavesSlot={
          <RequestClarificationWaves
            requestId={id}
            requestProgressId={requestProgress?.id}
          />
        }
      />
    );
  };

  return <div>{renderFormContent()}</div>;
};

export default MyRequestDetails;
